import { Paragraph, TextRun, TableRow, AlignmentType, PageOrientation } from "docx";
import * as m from "@app/paraglide/messages.js";
import {
    createHeaderCell,
    createSimpleHeaderCell,
    createDataCell,
    createEmptyCell,
    createRow,
    formatTimecode,
    formatDuration,
    getEventNames,
    buildContentWithTags,
    SHADING_DARK
} from "../helpers.js";
import convertToFormattedTextRuns from "../convertToFormattedTextRuns.js";

/**
 * Default CCSL template.
 * 9 columns with a two-row header (top row has spanning group labels).
 * Dialogue events and spotting events are merged using rowSpan.
 * Uses all four document sections (head, metadata, main, footer).
 */
export default {
    id: "default",

    /**
     * Returns the document configuration for section assembly.
     */
    getDocumentConfig() {
        return {
            orientation: PageOrientation.LANDSCAPE,
            useSections: true,
            hasPageHeader: false
        };
    },

    /**
     * Returns the header rows for the default template.
     * Two rows: a spanning group header + individual column headers.
     * @returns {TableRow[]}
     */
    getHeaders() {
        // Row 1: group header spanning columns
        const groupRow = new TableRow({
            tableHeader: true,
            children: [
                createSimpleHeaderCell(
                    m.ccsl_export_header_combined_continuity_dialogue(),
                    AlignmentType.CENTER, SHADING_DARK, 4
                ),
                createEmptyCell(SHADING_DARK),
                createSimpleHeaderCell(
                    m.ccsl_export_header_spotting_list(),
                    AlignmentType.CENTER, SHADING_DARK, 4
                )
            ]
        });

        // Row 2: individual column headers
        const columnRow = new TableRow({
            tableHeader: true,
            children: [
                createSimpleHeaderCell(m.ccsl_export_header_shot_number(), AlignmentType.CENTER, SHADING_DARK),
                createSimpleHeaderCell(m.ccsl_export_header_shot_timing(), AlignmentType.CENTER, SHADING_DARK),
                createSimpleHeaderCell(m.ccsl_export_header_shot_description_titlecase(), AlignmentType.CENTER, SHADING_DARK),
                createSimpleHeaderCell(m.ccsl_export_header_dialogue_titlecase(), AlignmentType.CENTER, SHADING_DARK),
                createSimpleHeaderCell(m.ccsl_export_header_title_number_spaced(), AlignmentType.CENTER, SHADING_DARK),
                createSimpleHeaderCell(m.ccsl_export_header_timecode_in(), AlignmentType.CENTER, SHADING_DARK),
                createSimpleHeaderCell(m.ccsl_export_header_timecode_out(), AlignmentType.CENTER, SHADING_DARK),
                createSimpleHeaderCell(m.ccsl_export_header_duration_titlecase(), AlignmentType.CENTER, SHADING_DARK),
                createSimpleHeaderCell(m.ccsl_export_header_dialogue_with_speaker_id(), AlignmentType.CENTER, SHADING_DARK),
            ]
        });

        return [groupRow, columnRow];
    },

    /**
     * Builds table rows for a dialogue event that has matching spotting events.
     * Uses rowSpan to merge dialogue columns across spotting event rows.
     * @param {object} event - The dialogue event
     * @param {number} index - Event index in the dialogue event group
     * @param {Array} spottingEvents - Filtered spotting events within this dialogue event's time range
     * @param {object} spottingEventGroup - The full spotting event group (for index lookup)
     * @param {object} project - Project state { incode, frameRate, dropFrame }
     * @returns {TableRow[]}
     */
    buildDialogueRowWithSpotting(event, index, spottingEvents, spottingEventGroup, project) {
        const rows = [];

        spottingEvents.forEach((spottingEvent, spIndex) => {
            const spottingEventIndex = spottingEventGroup.events.findIndex(e => e.id === spottingEvent.id);
            const { speakerNames, recipientNames } = getEventNames(spottingEvent);

            const cells = [];

            // First spotting row includes the dialogue columns with rowSpan
            if (spIndex === 0) {
                cells.push(
                    createDataCell((index + 1).toString(), { rowSpan: spottingEvents.length }),
                    createDataCell(formatTimecode(event.start, project), { rowSpan: spottingEvents.length }),
                    createDataCell(buildContentWithTags(event.tags, event.notes), { rowSpan: spottingEvents.length }),
                    createDataCell(
                        new Paragraph({ children: convertToFormattedTextRuns(event.text) }),
                        { rowSpan: spottingEvents.length }
                    ),
                );
            }

            // Build bold speaker label runs for the Dialogue With Speaker ID column
            const speakerLabelRuns = [];
            if (speakerNames) {
                speakerLabelRuns.push(
                    new TextRun({
                        text: recipientNames
                            ? m.ccsl_export_speaker_to_recipient({ speakers: speakerNames, recipients: recipientNames })
                            : m.ccsl_export_speaker_only({ speakers: speakerNames }),
                        bold: true
                    }),
                    new TextRun({ break: 1 })
                );
            } else if (recipientNames) {
                speakerLabelRuns.push(
                    new TextRun({ text: m.ccsl_export_recipient_only({ recipients: recipientNames }), bold: true }),
                    new TextRun({ break: 1 })
                );
            }

            // Spotting columns (every row)
            cells.push(
                createDataCell((spottingEventIndex + 1).toString(), { rowSpan: 1, shading: SHADING_DARK }),
                createDataCell(formatTimecode(spottingEvent.start, project), { rowSpan: 1 }),
                createDataCell(formatTimecode(spottingEvent.end, project), { rowSpan: 1 }),
                createDataCell(formatDuration(spottingEvent.start, spottingEvent.end, project), { rowSpan: 1 }),
                createDataCell(
                    buildContentWithTags(spottingEvent.tags, new Paragraph({ children: [...speakerLabelRuns, ...convertToFormattedTextRuns(spottingEvent.text)] })),
                    { rowSpan: 1 }
                ),
            );

            rows.push(createRow(cells));
        });

        return rows;
    },

    /**
     * Builds a table row for a dialogue event with no matching spotting events.
     * @param {object} event - The dialogue event
     * @param {number} index - Event index
     * @param {object} project - Project state
     * @returns {TableRow[]}
     */
    buildDialogueRowWithoutSpotting(event, index, project) {
        return [createRow([
            createDataCell((index + 1).toString()),
            createDataCell(formatTimecode(event.start, project)),
            createDataCell(buildContentWithTags(event.tags, event.notes)),
            createDataCell(new Paragraph({ children: convertToFormattedTextRuns(event.text) })),
            createEmptyCell(SHADING_DARK),
            createEmptyCell(),
            createEmptyCell(),
            createEmptyCell(),
            createEmptyCell(),
        ])];
    }
};
